import { ArrowRight, CheckCircle, FileText, Coins, Sparkles, UploadCloud } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';

const stats = [
  { value: 'Dynamic',  label: 'Field Names'        },
  { value: 'Offline',  label: 'No Cloud AI'        },
  { value: 'JSON',     label: 'Structured Output'  },
  { value: 'Private',  label: 'Data Not Stored'    },
];

const docLines = [
  { w: 'w-20', accent: false },
  { w: 'w-full', accent: false },
  { w: 'w-5/6',  accent: false },
  { w: 'w-3/4',  accent: true  },
  { w: 'w-full', accent: false },
  { w: 'w-4/5',  accent: true  },
  { w: 'w-full', accent: false },
  { w: 'w-2/3',  accent: false },
];

const extracted = [
  { label: 'invoice_number', value: 'INV-2026-0001'       },
  { label: 'vendor_name',   value: 'PT Tyo Solution'      },
  { label: 'total_amount',  value: 'Rp 8,214,000'         },
  { label: 'due_date',      value: '2026-05-25'           },
];

function OCRDemoCard() {
  return (
    <div className='relative animate-float'>
      {/* Main card */}
      <div className='glass-card rounded-2xl p-6 w-80 border border-border relative'>
        {/* Window chrome */}
        <div className='flex items-center gap-2 mb-5'>
          <div className='flex gap-1.5'>
            <span className='w-3 h-3 rounded-full bg-[hsl(0_84%_70%)]' />
            <span className='w-3 h-3 rounded-full bg-credit' />
            <span className='w-3 h-3 rounded-full bg-success' />
          </div>
          <div className='flex items-center gap-1.5 ml-2 flex-1 min-w-0'>
            <FileText className='w-3.5 h-3.5 text-muted-foreground flex-shrink-0' />
            <span className='text-xs text-muted-foreground font-mono truncate'>invoice_2024.pdf</span>
          </div>
          <div className='flex items-center gap-1 text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full flex-shrink-0'>
            <span className='w-1.5 h-1.5 rounded-full bg-success animate-pulse-dot' />
            Live
          </div>
        </div>

        {/* Scan area */}
        <div className='relative bg-surface rounded-xl overflow-hidden mb-4' style={{ height: '138px' }}>
          {/* Scan wrapper - animates top 0 → 100% */}
          <div className='absolute left-0 right-0 z-10 animate-scan-line'>
            <div className='h-6 bg-gradient-to-b from-accent/25 to-transparent' />
            <div className='h-0.5 bg-accent rounded-full shadow-accent' />
          </div>
          {/* Document text lines */}
          <div className='p-4 space-y-2.5 relative z-0'>
            {docLines.map((l, i) => (
              <div
                key={i}
                className={cn(
                  'h-1.5 rounded-full',
                  l.w,
                  l.accent ? 'bg-primary/35' : 'bg-muted-foreground/20'
                )}
              />
            ))}
          </div>
        </div>

        {/* Extracted data */}
        <div className='bg-primary/5 border border-primary/10 rounded-xl p-3.5 mb-3'>
          <div className='flex items-center gap-1.5 mb-2.5'>
            <CheckCircle className='w-3.5 h-3.5 text-accent' />
            <span className='text-xs font-semibold text-accent'>Fields Extracted</span>
          </div>
          <div className='space-y-1.5'>
            {extracted.map(({ label, value }) => (
              <div key={label} className='flex justify-between items-center'>
                <span className='text-xs text-muted-foreground'>{label}</span>
                <span className='text-xs font-mono font-medium text-foreground'>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Credit bar */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-1.5'>
            <div className='w-5 h-5 rounded-full bg-credit/15 flex items-center justify-center'>
              <Coins className='w-3 h-3 text-credit' />
            </div>
            <span className='text-xs text-muted-foreground'>1 credit used</span>
          </div>
          <span className='text-xs font-semibold text-credit'>499 remaining</span>
        </div>
      </div>

        {/* Dynamic fields badge */}
      <div className='absolute -top-3.5 -right-4 bg-accent text-accent-foreground text-xs px-3 py-1.5 rounded-full font-semibold shadow-accent'>
        Your fields, your rules
      </div>

      {/* Confidence badge */}
      <div className='absolute -bottom-4 -left-4 bg-card shadow-card border border-border rounded-xl px-3 py-2 flex items-center gap-2'>
        <div className='w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center'>
          <CheckCircle className='w-4 h-4 text-primary' />
        </div>
        <div>
          <p className='text-xs font-semibold text-foreground'>Confidence score</p>
          <p className='text-xs text-muted-foreground'>per field, 0–1.0</p>
        </div>
      </div>
    </div>
  );
}

export default function HeroSection({ onGetStarted }) {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className='relative min-h-screen bg-gradient-hero flex items-center overflow-hidden pt-16'>
      {/* Background decoration */}
      <div className='absolute inset-0 dot-pattern-hero pointer-events-none' />
      <div className='absolute top-1/4 left-1/4 w-80 h-80 bg-accent/12 rounded-full blur-3xl pointer-events-none' />
      <div className='absolute bottom-1/3 right-1/3 w-64 h-64 bg-primary-light/15 rounded-full blur-3xl pointer-events-none' />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full'>
        <div className='grid lg:grid-cols-2 gap-12 lg:gap-20 items-center'>

          {/* ── Left: content ── */}
          <div className='space-y-8'>
            {/* Badge */}
            <div className='inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/15 border border-accent/30 text-accent-light text-sm font-medium'>
              <Sparkles className='w-4 h-4' />
              Fully Offline · No Cloud AI · Privacy-First
            </div>

            {/* Headline */}
            <h1 className='font-heading font-bold text-5xl lg:text-6xl leading-tight text-primary-foreground'>
              Extract Any Field From{' '}
              <span className='text-accent-light'>Any Invoice</span>
            </h1>

            {/* Subtitle */}
            <p className='text-lg text-primary-foreground/75 leading-relaxed max-w-lg'>
              Define the field names you need — vendor, amount, due date, anything.
              Get a structured JSON response with confidence scores. Documents never leave your server.
            </p>

            {/* CTAs */}
            <div className='flex flex-col sm:flex-row gap-4'>
              <Button
                variant='accent'
                size='lg'
                onClick={onGetStarted}
                className='shadow-accent gap-2'
              >
                Start Free — 10 Bonus Credits
                <ArrowRight className='w-5 h-5' />
              </Button>
              <Button
                variant='hero'
                size='lg'
                onClick={() => scrollTo('how-it-works')}
              >
                See How it Works
              </Button>
            </div>

            {/* Stats row */}
            <div className='flex flex-wrap gap-8 pt-2'>
              {stats.map((s) => (
                <div key={s.label}>
                  <p className='text-2xl font-heading font-bold text-primary-foreground'>{s.value}</p>
                  <p className='text-sm text-primary-foreground/55'>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: OCR Demo ── */}
          <div className='hidden lg:flex items-center justify-center'>
            <OCRDemoCard />
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className='absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-background/20 to-transparent pointer-events-none' />
    </section>
  );
}
